import React, {useEffect, useReducer, useState} from 'react'
import {_L, useLocaleState} from '../../../context/LocaleContext'
import {useUIState} from '../../../context/UIContext'
import {fetchRooms} from '../../../api/room'
import Accordion from '../../../components/Accordion/Accordion'
import AccordionPanel from '../../../components/Accordion/AccordionPanel'
import styled from 'styled-components'
import Icons from '../../../components/Icons'
import RoomSelectFilterItem from './RoomSelectFilterItem'
import {useViewFetch} from '../../../utils/fetch'

const initialState = {
  activeIndex: 3, // {0: Employee LIst, 1: Locations by Name 2: Locations by Type, 3: Departments}. Default = Departments
  roomTypes: [],
  departments: [],
  selectedEm: '',
  selectedRm: '',
  selectedRmTypes: [],
  selectedDps: []
}

const reducer = (state, action) => {
  if (action.type === 'setData') {
    return {
      ...state,
      ...action.payload
    }
  } else {
    throw new Error('Unexpected action')
  }
}

const RoomSelectFilter = ({
  employees,
  clearSelectedEmployee,
  roomTypesByFloor,
  departmentsByFloor,
  onActiveFilterList,
  onSelectFilterItem,
  showClose,
  onCloseFilter
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const localeState = useLocaleState()
  const uiState = useUIState()

  const [filteredEmployees, setFilteredEmployees] = useState([])
  const [filteredRooms, setFilteredRooms] = useState([])
  const [searchEmployeeValue, setSearchEmployeeValue] = useState('')
  const [searchRoomValue, setSearchRoomValue] = useState('')

  const {
    activeIndex,
    selectedEm,
    selectedRm,
    selectedRmTypes,
    selectedDps
  } = state
  const {activeFloor} = uiState

  const fetchRoomsList = async ({activeFloor}) => {
    const roomsData = await fetchRooms(activeFloor)
    return Object.keys(roomsData)
      .map(item => {
        let room = roomsData[item]
        const {rmId, name, rmTypeDesc} = room
        const label = `${rmId} - ${name || rmTypeDesc}`
        return {...room, label}
      })
      .sort((a, b) => a.rmId.localeCompare(b.rmId))
  }

  const {data: rooms} = useViewFetch({
    api: fetchRoomsList,
    parameters: {activeFloor},
    initialState: [],
    onChangeOf: [activeFloor, activeIndex]
  })

  useEffect(
    () => {
      if (!clearSelectedEmployee) {
        dispatch({
          type: 'setData',
          payload: {
            selectedEm: selectedEm
          }
        })
      }
      return () => {
        // clear selected filter values on floor change
        dispatch({
          type: 'setData',
          payload: {
            selectedEm: '',
            selectedRm: '',
            selectedRmTypes: [],
            selectedDps: []
          }
        })
      }
    },
    [activeFloor]
  )

  useEffect(
    () => {
      let isMounted = true
      if (isMounted) {
        if (searchEmployeeValue) {
          filterEmployees(searchEmployeeValue)
        } else {
          setFilteredEmployees(employees)
        }
        if (searchRoomValue) {
          filterRooms(searchRoomValue)
        } else {
          setFilteredRooms(rooms)
        }
      }
      return () => {
        isMounted = false
      }
    },
    [employees, rooms]
  )

  const onAccordionActive = activeIndex => {
    dispatch({
      type: 'setData',
      payload: {
        activeIndex: activeIndex[0]
      }
    })
    onActiveFilterList(activeIndex[0])
  }

  const filterEmployees = value => {
    let upperCaseValue = value.toUpperCase()
    let filteredEmployees = [...employees]
    if (value) {
      filteredEmployees = employees.filter(
        employee =>
          employee.emId.toUpperCase().includes(upperCaseValue) ||
          employee.name.toUpperCase().includes(upperCaseValue) ||
          employee.nameFirst.toUpperCase().includes(upperCaseValue) ||
          employee.nameLast.toUpperCase().includes(upperCaseValue) ||
          employee.emTitle.toUpperCase().includes(upperCaseValue) ||
          employee.rmId.toUpperCase().includes(upperCaseValue) ||
          employee.flId.toUpperCase().includes(upperCaseValue) ||
          employee.dpId.toUpperCase().includes(upperCaseValue) ||
          employee.dpName.toUpperCase().includes(upperCaseValue)
      )
    }

    setSearchEmployeeValue(value)
    setFilteredEmployees(filteredEmployees)
  }

  const filterRooms = value => {
    let upperCaseValue = value.toUpperCase()
    let filteredRooms = rooms
    if (value) {
      filteredRooms = rooms.filter(
        room =>
          room.rmId.toUpperCase().includes(upperCaseValue) ||
          room.name.toUpperCase().includes(upperCaseValue) ||
          room.rmType.toUpperCase().includes(upperCaseValue) ||
          room.rmTypeDesc.toUpperCase().includes(upperCaseValue)
      )
    }

    setSearchRoomValue(value)
    setFilteredRooms(filteredRooms)
  }

  const onItemClick = ({type, item, evt}) => {
    if (evt) {
      evt.stopPropagation()
    }
    let showItem = false
    let emId = ''
    let rmId = ''
    let rmTypes = []
    let dps = []
    if (type === 'em') {
      emId = item.emId
    } else if (type === 'rm') {
      rmId = item.rmId
    } else if (type === 'rmtype') {
      let {rmType} = item
      let {items, show} = getSelectedItems(rmType, selectedRmTypes)
      rmTypes = items
      showItem = show
    } else if (type === 'dp') {
      let {dp_id} = item
      let {items, show} = getSelectedItems(dp_id, selectedDps)
      dps = items
      showItem = show
    }
    dispatch({
      type: 'setData',
      payload: {
        selectedEm: emId,
        selectedRm: rmId,
        selectedRmTypes: rmTypes,
        selectedDps: dps
      }
    })
    const newObj = Object.assign({show: showItem}, item)
    onSelectFilterItem({type, item: newObj})
    if (showClose) {
      onCloseFilter()
    }
  }

  const getSelectedItems = (itemId, selectedItems) => {
    let show = false
    const items = [...selectedItems]

    let index = items.findIndex(id => id === itemId)

    if (items.includes(itemId)) {
      // already selected remove it
      items.splice(index, 1)
    } else {
      items.push(itemId)
      show = true
    }
    return {items, show}
  }

  const showSearch = true

  return (
    <Container>
      {showClose && (
        <div>
          <Title>{_L({key: 'Search locations', localeState})}</Title>
          <CloseIcon onClick={onCloseFilter}>
            <Icons icon="CLOSE" height={14} width={14} />
          </CloseIcon>
        </div>
      )}
      <Accordion onActive={onAccordionActive} activeIndexes={[activeIndex]}>
        <AccordionPanel label={_L({key: 'Employee List', localeState})}>
          <StyledSearch show={showSearch}>
            <Icons icon="SEARCH-GREY" />
            <StyledSearchInput
              type={'search'}
              placeholder={_L({key: 'Search building employees', localeState})}
              onChange={e => {
                e.preventDefault()
                filterEmployees(e.target.value)
              }}
              value={searchEmployeeValue}
            />
          </StyledSearch>
          <ListContainer>
            {filteredEmployees.map((employee, index) => (
              <RoomSelectFilterItem
                key={employee.emId + index}
                employee={employee}
                onItemClick={onItemClick}
                selected={employee.emId === selectedEm}
                type={'em'}
                {...employee}
              />
            ))}
          </ListContainer>
        </AccordionPanel>

        <AccordionPanel label={_L({key: 'Locations by Name', localeState})}>
          <StyledSearch show={showSearch}>
            <Icons icon="SEARCH-GREY" />
            <StyledSearchInput
              type={'search'}
              placeholder={_L({key: 'Search locations on floor', localeState})}
              onChange={e => {
                e.preventDefault()
                filterRooms(e.target.value)
              }}
              value={searchRoomValue}
            />
          </StyledSearch>
          <ListContainer>
            {filteredRooms.map((room, index) => (
              <RoomSelectFilterItemContainer key={room.rmId + index}>
                <RoomSelectFilterItem
                  label={room.label}
                  onItemClick={onItemClick}
                  selected={room.rmId === selectedRm}
                  type={'rm'}
                  {...room}
                />
              </RoomSelectFilterItemContainer>
            ))}
          </ListContainer>
        </AccordionPanel>

        <AccordionPanel label={_L({key: 'Locations by Type', localeState})}>
          <ListContainer>
            {Object.values(roomTypesByFloor).map((item, index) => {
              const {rmType, rmTypeDesc, color} = item
              return (
                rmType && (
                  <RoomSelectFilterItemContainer key={rmType + index}>
                    <RoomSelectFilterItem
                      label={rmTypeDesc}
                      color={color}
                      onItemClick={onItemClick}
                      selected={selectedRmTypes.includes(rmType)}
                      type={'rmtype'}
                      {...item}
                    />
                  </RoomSelectFilterItemContainer>
                )
              )
            })}
          </ListContainer>
        </AccordionPanel>

        <AccordionPanel label={_L({key: 'Departments', localeState})}>
          <ListContainer>
            {Object.values(departmentsByFloor).map((item, index) => {
              const {dp_id, color, department} = item
              return (
                dp_id && (
                  <RoomSelectFilterItemContainer key={dp_id + index}>
                    <RoomSelectFilterItem
                      label={department || dp_id}
                      color={color}
                      onItemClick={onItemClick}
                      selected={selectedDps.includes(dp_id)}
                      type={'dp'}
                      {...item}
                    />
                  </RoomSelectFilterItemContainer>
                )
              )
            })}
          </ListContainer>
        </AccordionPanel>
      </Accordion>
    </Container>
  )
}

export default RoomSelectFilter

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  width: 100%;
  height: calc(100vh - 80px);
`

const ListContainer = styled.div`
  background-color: white;
  padding: 4px 20px 10px 20px;
  box-sizing: border-box;
  border-radius: 0 0 5px 5px;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  max-height: 375px;
`
const StyledSearch = styled.div`
  margin: 8px 20px 22px 20px;
  background: #f7f8fc;
  border: 1px solid #f3f5fb;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  display: ${props => (props.show ? 'flex' : 'none')};
  align-items: center;
`

const StyledSearchInput = styled.input`
  background: #f7f8fc;
  border: 0;
  padding-left: 14px;
  color: #a4a9b7;
  font-weight: normal;
  height: 19px;
  width: 100%;
`

const RoomSelectFilterItemContainer = styled.div`
  display: flex;
  justify-content: center;
`

const Title = styled.div`
  font-size: 22px;
  height: 60px;
  padding: 26px 24px 24px 24px;
  box-sizing: border-box;
  font-weight: bold;
  line-height: 26px;
`

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  width: 38px;
  border-radius: 3px;
  background-color: #f4f5fa;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 25;
`
